/**
 * Snapped module groups.
 *
 * Dropping a module so one of its edges sits flush against another module's
 * edge stores `box.attach` instead of relying on its own x/y alone:
 *
 *   attach: { to, side, align, gap, offset }
 *
 * - to:     the anchor module id
 * - side:   where this module sits relative to the anchor (left | right | top | bottom)
 * - align:  which cross-axis edges line up (start | center | end)
 * - gap:    distance between the touching edges, in em of this module
 * - offset: extra cross-axis shift when no edge lines up, in em of this module
 *
 * A group made with Group (⌘G) stores `{ to, side: 'origin', gap, offset, group: true }`:
 * the module sits `gap` right of and `offset` below the anchor's top-left
 * corner, so the arrangement holds at every size without edges touching.
 *
 * The box keeps its x/y as the first-paint fallback. At runtime
 * `applyModuleAttachments` measures the anchor and nudges the module with the
 * CSS `translate` property, so a group follows its anchor at every viewport
 * size and whenever the anchor moves or resizes.
 */

import { moduleBox, modulesMapFromBlocks } from './header-modules.js';

export const ATTACH_SIDES = ['left', 'right', 'top', 'bottom'];
export const ATTACH_ALIGNS = ['start', 'center', 'end'];
/** Side of a group bond: offset from the anchor's top-left corner. */
export const GROUP_SIDE = 'origin';

/** How far apart touching edges may be, in px, and still count as snapped. */
export const ATTACH_TOLERANCE = 1.5;

const VIEWER_PREFIXES = ['icon-', 'video-'];

/**
 * Header blocks scroll with the page; viewer chrome is fixed to the viewport.
 * Only modules of the same family can form a group.
 * @param {string} id
 * @returns {'viewer'|'header'}
 */
export function moduleFamily(id) {
    const key = String(id || '');
    if (
        key === 'viewer-logo'
        || key === 'media-frame'
        || key === 'media-title'
        || VIEWER_PREFIXES.some((prefix) => key.startsWith(prefix))
    ) {
        return 'viewer';
    }
    return 'header';
}

/** A usable attach record, or null. */
export function normalizeAttach(attach) {
    if (!attach || typeof attach !== 'object' || !attach.to) return null;
    if (attach.group === true || attach.side === GROUP_SIDE) {
        return {
            to: String(attach.to),
            side: GROUP_SIDE,
            align: 'start',
            gap: attach.gap || '0em',
            offset: attach.offset || '0em',
            group: true,
        };
    }
    if (!ATTACH_SIDES.includes(attach.side)) return null;
    return {
        to: String(attach.to),
        side: attach.side,
        align: ATTACH_ALIGNS.includes(attach.align) ? attach.align : 'start',
        gap: attach.gap || '0em',
        offset: attach.offset || '0em',
    };
}

/** A stored gap/offset in px. Bare numbers and em are of the module's font size. */
function lengthPx(value, em, rem) {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return 0;
    const unit = typeof value === 'string' ? (value.trim().match(/[a-z]+$/i)?.[0] || 'em') : 'em';
    if (unit === 'px') return n;
    if (unit === 'rem') return n * rem;
    return n * em;
}

function roundEm(px, em) {
    const safe = em > 0 ? em : 16;
    return `${Math.round((px / safe) * 1000) / 1000}em`;
}

/**
 * Module ids that would be moved by `id` (directly or through a chain).
 * @param {(id: string) => Object|null} attachOf - attach record for a module id
 * @param {string} id
 * @param {string[]} ids - every module id to consider
 * @returns {Set<string>}
 */
export function attachedFollowers(attachOf, id, ids) {
    const followers = new Set();
    let grew = true;
    while (grew) {
        grew = false;
        ids.forEach((candidate) => {
            if (candidate === id || followers.has(candidate)) return;
            const to = attachOf(candidate)?.to;
            if (to && (to === id || followers.has(to))) {
                followers.add(candidate);
                grew = true;
            }
        });
    }
    return followers;
}

/**
 * Viewport position for a module of `size` attached to `anchorRect`.
 * @param {DOMRect|Object} anchorRect
 * @param {{ width: number, height: number }} size
 * @param {Object} attach - normalized attach record
 * @param {number} em - font size of the attached module, px
 * @param {number} [rem] - root font size, px
 * @returns {{ left: number, top: number }}
 */
export function attachPosition(anchorRect, size, attach, em = 16, rem = 16) {
    const gap = lengthPx(attach.gap, em, rem);
    const offset = lengthPx(attach.offset, em, rem);
    if (attach.side === GROUP_SIDE) {
        return { left: anchorRect.left + gap, top: anchorRect.top + offset };
    }
    const horizontal = attach.side === 'left' || attach.side === 'right';
    let left;
    let top;
    if (horizontal) {
        left = attach.side === 'right'
            ? anchorRect.left + anchorRect.width + gap
            : anchorRect.left - gap - size.width;
        if (attach.align === 'center') top = anchorRect.top + (anchorRect.height - size.height) / 2;
        else if (attach.align === 'end') top = anchorRect.top + anchorRect.height - size.height;
        else top = anchorRect.top;
        top += offset;
    } else {
        top = attach.side === 'bottom'
            ? anchorRect.top + anchorRect.height + gap
            : anchorRect.top - gap - size.height;
        if (attach.align === 'center') left = anchorRect.left + (anchorRect.width - size.width) / 2;
        else if (attach.align === 'end') left = anchorRect.left + anchorRect.width - size.width;
        else left = anchorRect.left;
        left += offset;
    }
    return { left, top };
}

function overlap(a0, a1, b0, b1) {
    return Math.min(a1, b1) - Math.max(a0, b0);
}

function alignFor(start, center, end, anchorStart, anchorCenter, anchorEnd, tolerance) {
    const options = [
        { align: 'start', d: Math.abs(start - anchorStart) },
        { align: 'center', d: Math.abs(center - anchorCenter) },
        { align: 'end', d: Math.abs(end - anchorEnd) },
    ].sort((a, b) => a.d - b.d);
    return options[0].d <= tolerance ? options[0].align : null;
}

/**
 * Find the module a dropped rect is in contact with.
 *
 * With `outline` > 0 each module is treated as its rect grown by that many px
 * (the editor's selection outline). Two modules are in contact when their
 * outlines meet or overlap on one side, including corner to corner, without
 * the modules themselves overlapping. The stored gap is the real distance, so
 * outlines that met on drop still meet at runtime.
 * @param {{ left: number, top: number, width: number, height: number }} rect - dropped module
 * @param {{ id: string, rect: Object }[]} others - candidate anchors (same family, no cycles)
 * @param {{ em?: number, tolerance?: number, outline?: number }} [opts]
 * @returns {Object|null} attach record
 */
export function detectAttach(rect, others, { em = 16, tolerance = ATTACH_TOLERANCE, outline = 0, exact = false } = {}) {
    const right = rect.left + rect.width;
    const bottom = rect.top + rect.height;
    const midX = rect.left + rect.width / 2;
    const midY = rect.top + rect.height / 2;
    const reach = Math.max(0, outline) * 2;
    let best = null;

    (others || []).forEach(({ id, rect: o }) => {
        if (!o || o.width <= 0 || o.height <= 0) return;
        const oRight = o.left + o.width;
        const oBottom = o.top + o.height;
        const crossY = overlap(rect.top - outline, bottom + outline, o.top - outline, oBottom + outline);
        const crossX = overlap(rect.left - outline, right + outline, o.left - outline, oRight + outline);
        const sides = [
            { side: 'right', gap: rect.left - oRight, cross: crossY },
            { side: 'left', gap: o.left - right, cross: crossY },
            { side: 'bottom', gap: rect.top - oBottom, cross: crossX },
            { side: 'top', gap: o.top - bottom, cross: crossX },
        ];
        sides.forEach(({ side, gap, cross }) => {
            if (gap < -tolerance || gap > reach + tolerance) return;
            if (reach ? cross < -tolerance : cross <= 0) return;
            const horizontal = side === 'left' || side === 'right';
            const align = horizontal
                ? alignFor(rect.top, midY, bottom, o.top, o.top + o.height / 2, oBottom, tolerance + 1)
                : alignFor(rect.left, midX, right, o.left, o.left + o.width / 2, oRight, tolerance + 1);
            const flush = Math.abs(gap) <= tolerance;
            const outlined = reach > 0 && Math.abs(gap - reach) <= tolerance;
            const miss = Math.min(Math.abs(gap), Math.abs(gap - reach));
            // Aligned edges beat a bare touch, a side beats a corner, then the closest contact.
            const score = miss + (align ? 0 : 1000) + (cross > tolerance ? 0 : 500);
            if (best && best.score <= score) return;
            let gapPx = gap;
            if (!exact && flush) gapPx = 0;
            else if (!exact && outlined) gapPx = reach;
            let offsetPx = align
                ? 0
                : (horizontal ? rect.top - o.top : rect.left - o.left);
            if (exact && align) {
                // Keep the dropped position to the pixel instead of snapping onto the edge.
                const [start, size, oStart, oSize] = horizontal
                    ? [rect.top, rect.height, o.top, o.height]
                    : [rect.left, rect.width, o.left, o.width];
                let aligned = oStart;
                if (align === 'center') aligned = oStart + (oSize - size) / 2;
                else if (align === 'end') aligned = oStart + oSize - size;
                offsetPx = start - aligned;
            }
            best = {
                score,
                attach: {
                    to: id,
                    side,
                    align: align || 'start',
                    gap: roundEm(gapPx, em),
                    offset: roundEm(offsetPx, em),
                },
            };
        });
    });

    return best ? best.attach : null;
}

/**
 * Group bond holding `rect` where it is relative to the leader.
 * @param {{ left: number, top: number }} rect - the member, as shown
 * @param {{ id: string, rect: { left: number, top: number } }} leader
 * @param {number} [em] - font size of the member, px
 */
export function groupBond(rect, leader, em = 16) {
    return {
        to: leader.id,
        side: GROUP_SIDE,
        align: 'start',
        gap: roundEm(rect.left - leader.rect.left, em),
        offset: roundEm(rect.top - leader.rect.top, em),
        group: true,
    };
}

/**
 * Every module in the same Group (⌘G) as `id`, including `id`.
 * @param {Map<string, Object>} attachments - from attachmentsFor
 * @param {string} id
 * @returns {Set<string>}
 */
export function moduleGroup(attachments, id) {
    const members = new Set(id ? [id] : []);
    let grew = Boolean(id);
    while (grew) {
        grew = false;
        attachments.forEach((attach, from) => {
            if (!attach?.group) return;
            if (members.has(from) === members.has(attach.to)) return;
            members.add(from);
            members.add(attach.to);
            grew = true;
        });
    }
    return members;
}

/**
 * `ids` plus everything grouped with them, in selection order.
 * @param {Map<string, Object>} attachments
 * @param {string[]} ids
 * @returns {string[]}
 */
export function expandGroups(attachments, ids) {
    const out = [];
    (ids || []).forEach((id) => {
        if (!id) return;
        if (!out.includes(id)) out.push(id);
        moduleGroup(attachments, id).forEach((member) => {
            if (!out.includes(member)) out.push(member);
        });
    });
    return out;
}

/**
 * Bond changes for Group (⌘G) and Ungroup (⇧⌘G).
 *
 * Group: the primary leads and every other module in the selection (and in
 * any group it touches) bonds to it where it is now. The leader keeps a bond
 * to a module outside, so the whole group still follows that module.
 * Ungroup: every group bond in the selection's groups is removed.
 *
 * @param {Object} input
 * @param {'group'|'ungroup'} input.action
 * @param {string[]} input.ids - selected module ids
 * @param {string} [input.primary]
 * @param {Map<string, Object>} input.attachments - from attachmentsFor
 * @param {(id: string) => { left: number, top: number }|null} input.rectOf
 * @param {(id: string) => number} [input.emOf]
 * @returns {{ id: string, attach: Object|null }[]} null clears the bond
 */
export function groupChanges({ action, ids, primary, attachments, rectOf, emOf = () => 16 }) {
    const members = expandGroups(attachments, ids);
    if (action === 'ungroup') {
        return members
            .filter((id) => attachments.get(id)?.group)
            .map((id) => ({ id, attach: null }));
    }
    if (action !== 'group' || members.length < 2) return [];
    const leaderId = members.includes(primary) ? primary : members[0];
    const leaderRect = rectOf(leaderId);
    if (!leaderRect) return [];
    const family = moduleFamily(leaderId);
    const bonded = members.filter((id) => id !== leaderId && moduleFamily(id) === family && rectOf(id));
    if (!bonded.length) return [];
    const out = [];
    const leaderAttach = attachments.get(leaderId);
    if (leaderAttach && (leaderAttach.group || bonded.includes(leaderAttach.to))) {
        out.push({ id: leaderId, attach: null });
    }
    bonded.forEach((id) => {
        out.push({ id, attach: groupBond(rectOf(id), { id: leaderId, rect: leaderRect }, emOf(id)) });
    });
    return out;
}

/** True when some module in `ids` is in a group. */
export function hasGroup(attachments, ids) {
    return (ids || []).some((id) => moduleGroup(attachments, id).size > 1);
}

/**
 * Header modules merged with typed header block boxes, the same map the
 * header stylesheet reads.
 * @param {Object} theme
 */
export function attachModulesMap(theme) {
    const header = theme?.header || {};
    return Array.isArray(header.blocks)
        ? modulesMapFromBlocks(header.blocks, header.modules)
        : (header.modules || {});
}

/**
 * Attach record for each module id at a breakpoint.
 * @param {Object} modules
 * @param {string} layer
 * @returns {Map<string, Object>}
 */
export function attachmentsFor(modules, layer = 'desktop') {
    const out = new Map();
    Object.keys(modules || {}).forEach((id) => {
        const entry = modules[id];
        if (!entry || typeof entry !== 'object') return;
        const box = moduleBox(modules, id, layer);
        if (!box || box.hidden) return;
        const attach = normalizeAttach(box.attach);
        if (attach && attach.to !== id) out.set(id, attach);
    });
    return out;
}

function visibleModules(doc, id) {
    return [...doc.querySelectorAll(`[data-header-module="${CSS.escape(id)}"]`)]
        .filter((el) => {
            const r = el.getBoundingClientRect();
            return r.width > 0 || r.height > 0;
        });
}

function readTranslate(el) {
    return {
        x: parseFloat(el.dataset.attachDx) || 0,
        y: parseFloat(el.dataset.attachDy) || 0,
    };
}

function writeTranslate(el, x, y) {
    const current = readTranslate(el);
    if (Math.abs(current.x - x) < 0.5 && Math.abs(current.y - y) < 0.5) return false;
    el.dataset.attachDx = String(Math.round(x * 10) / 10);
    el.dataset.attachDy = String(Math.round(y * 10) / 10);
    el.style.translate = `${Math.round(x * 10) / 10}px ${Math.round(y * 10) / 10}px`;
    return true;
}

function clearTranslate(el) {
    if (el.dataset.attachDx == null && el.dataset.attachDy == null) return;
    delete el.dataset.attachDx;
    delete el.dataset.attachDy;
    el.style.translate = '';
}

/**
 * Place every attached module next to its anchor.
 * Anchors are placed before their followers so a chain settles in one pass.
 * Modules being dragged (data-attach-dragging) are left alone; their followers still move.
 * @param {Document} doc
 * @param {Map<string, Object>} attachments - from attachmentsFor
 */
export function applyModuleAttachments(doc, attachments) {
    doc.querySelectorAll('[data-attach-dx], [data-attach-dy]').forEach((el) => {
        const id = el.dataset.headerModule;
        if (!attachments.has(id) || el.dataset.attachDragging === '1') clearTranslate(el);
    });

    const ids = [...attachments.keys()];
    const placed = new Set();
    const visiting = new Set();

    const place = (id) => {
        if (placed.has(id) || visiting.has(id)) return;
        visiting.add(id);
        const attach = attachments.get(id);
        if (attachments.has(attach.to)) place(attach.to);
        visiting.delete(id);
        placed.add(id);

        const anchor = visibleModules(doc, attach.to)[0];
        const targets = visibleModules(doc, id);
        targets.forEach((el) => {
            if (el.dataset.attachDragging === '1') return;
            // Viewer chrome (video controls, icons, the picture frame) is stored
            // in viewport units. An em bond was measured in the editor and does
            // not match those units on another screen, so applying it after
            // paint pulls the controls off the position that just rendered.
            if (!anchor || moduleFamily(attach.to) !== moduleFamily(id) || moduleFamily(id) === 'viewer') {
                clearTranslate(el);
                return;
            }
            const anchorRect = anchor.getBoundingClientRect();
            const rect = el.getBoundingClientRect();
            const current = readTranslate(el);
            const baseLeft = rect.left - current.x;
            const baseTop = rect.top - current.y;
            const em = parseFloat(getComputedStyle(el).fontSize) || 16;
            const rem = parseFloat(getComputedStyle(doc.documentElement).fontSize) || 16;
            const next = attachPosition(anchorRect, rect, attach, em, rem);
            writeTranslate(el, next.left - baseLeft, next.top - baseTop);
        });
    };

    ids.forEach(place);
}
